// src/components/CourseViewer.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ScormWrapper from './ScormWrapper';
import ModuleNavigation from './ModuleNavigation';
import { toast } from 'react-toastify';
import { Course, Module } from '../types';
import api, { getModuleProgress, updateModuleProgress } from '../services/api';

interface ModuleWithProgress extends Module {
  progress?: number;
  completed_at?: Date | null;
  started_at?: Date | null;
  is_unlocked?: boolean;
  is_current?: boolean;
}

interface CourseViewerProps {
  courseId?: string;
  moduleId?: string;
  course?: Course;
  onClose?: () => void;
}

export const CourseViewer: React.FC<CourseViewerProps> = ({ courseId, moduleId, course, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<ModuleWithProgress[]>([]);
  const [currentModuleId, setCurrentModuleId] = useState<number | null>(null);
  const [currentModule, setCurrentModule] = useState<ModuleWithProgress | null>(null);
  const [scormUrl, setScormUrl] = useState<string | null>(null);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const { user } = useAuth();

  // Load module progress when component mounts
  useEffect(() => {
    const loadModuleProgress = async () => {
      if (!course?.id) {
        setError('Course ID not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch module progress from API
        const response = await getModuleProgress(course.id);
        const modulesWithProgress = response.data.modules;

        // Add is_current flag and determine which module to show
        const processedModules = modulesWithProgress.map((module: ModuleWithProgress, index: number) => ({
          ...module,
          is_current: false
        }));

        // Determine current module
        let targetModuleId: number;
        if (moduleId && processedModules.find(m => m.id === parseInt(moduleId))) {
          // Use specified module if valid and unlocked
          const specifiedModule = processedModules.find(m => m.id === parseInt(moduleId));
          targetModuleId = specifiedModule?.is_unlocked ? parseInt(moduleId) : processedModules[0].id;
        } else {
          // Find the first unlocked module that isn't completed, or the first module
          const nextModule = processedModules.find(m => m.is_unlocked && !m.completed_at);
          targetModuleId = nextModule ? nextModule.id : processedModules[0].id;
        }

        // Mark current module and update state
        const finalModules = processedModules.map(module => ({
          ...module,
          is_current: module.id === targetModuleId
        }));

        setModules(finalModules);
        setCurrentModuleId(targetModuleId);
        
        // Load the current module
        await loadModule(targetModuleId, finalModules);

      } catch (error) {
        console.error('[CourseViewer] Error loading module progress:', error);
        setError('Failed to load course modules');
        setLoading(false);
      }
    };

    if (course?.id) {
      loadModuleProgress();
    }
  }, [course?.id, moduleId]);

  // Function to load a specific module
  const loadModule = async (moduleId: number, modulesList?: ModuleWithProgress[]) => {
    try {
      const moduleList = modulesList || modules;
      const targetModule = moduleList.find(m => m.id === moduleId);

      if (!targetModule) {
        throw new Error('Module not found');
      }

      setCurrentModule(targetModule);
      setScormUrl(null);
      setError(null);

      // Mark module as started if not already
      if (!targetModule.started_at) {
        try {
          await updateModuleProgress(moduleId, 1, course!.id);
        } catch (progressError) {
          console.error('[CourseViewer] Failed to mark module as started:', progressError);
        }
      }

      // If SCORM, fetch launch URL
      if (targetModule.is_scorm) {
        setLoading(true);
        try {
          const response = await api.get(`/scorm/${moduleId}/launch`);
          
          if (!response.data?.url) {
            throw new Error('SCORM launch URL not found in response');
          }
          
          setScormUrl(response.data.url);
        } catch (err: any) {
          const errorStatus = err.response?.status;
          const errorResponseData = err.response?.data;
          
          if (errorStatus === 202 && errorResponseData?.status === 'processing') {
            const errorMessage = errorResponseData.message || 'SCORM module is still being processed. Please wait and try again.';
            setError(errorMessage);
            toast.info(errorMessage);
            
            // Set up automatic retry after 30 seconds
            setTimeout(() => {
              console.log('[CourseViewer] Auto-retrying SCORM launch...');
              loadModule(moduleId);
            }, 30000);
            
            return;
          }
          
          const errorMessage = errorResponseData?.message || 'Failed to load SCORM content';
          setError(errorMessage);
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }

      // Scroll to top when module loads
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load module';
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  // Handle module selection from navigation
  const handleModuleSelect = async (moduleId: number) => {
    const targetModule = modules.find(m => m.id === moduleId);
    
    // CHANGED: Remove unlocking restriction since all modules are now available
    // if (!targetModule?.is_unlocked && !targetModule?.completed_at) {
    //   toast.warning('This module is locked. Complete the previous modules first.');
    //   return;
    // }

    // Update current module in state
    const updatedModules = modules.map(module => ({
      ...module,
      is_current: module.id === moduleId
    }));

    setModules(updatedModules);
    setCurrentModuleId(moduleId);

    // Load the selected module
    await loadModule(moduleId, updatedModules);
  };

  // Handle module completion
  const handleModuleCompletion = async () => {
    if (!currentModuleId || !course?.id || completionLoading) return;

    try {
      setCompletionLoading(true);
      console.log('[CourseViewer] Module completion detected for module:', currentModuleId);

      // ScormWrapper already persisted 100% / completeModule.
      // Refresh module progress so the sidebar and next-module state update.
      const response = await getModuleProgress(course.id);
      const updatedModules = response.data.modules.map((module: ModuleWithProgress) => ({
        ...module,
        is_current: false // Reset current flag
      }));

      // Check if course is fully completed
      const allCompleted = updatedModules.every(m => m.completed_at);
      if (allCompleted) {
        setCourseCompleted(true);
        setModules(updatedModules);
        toast.success('🎉 Course completed! Congratulations!');
      } else {
        // CHANGED: Don't auto-navigate to next module since users can choose freely
        setModules(updatedModules);
        toast.success('Module completed! You can now select any other module to continue.');
        
        // Optional: Still show suggestion about next module
        const nextModule = updatedModules.find(m => !m.completed_at);
        if (nextModule) {
          console.log('[CourseViewer] Next available module:', nextModule.title);
        }
        
        // Previous auto-navigation logic (commented out):
        // const nextModule = updatedModules.find(m => m.is_unlocked && !m.completed_at);
        // if (nextModule) {
        //   console.log('[CourseViewer] Auto-navigating to next module:', nextModule.id, nextModule.title);
        //   const modulesWithCurrent = updatedModules.map(module => ({
        //     ...module,
        //     is_current: module.id === nextModule.id
        //   }));
        //   setModules(modulesWithCurrent);
        //   setCurrentModuleId(nextModule.id);
        //   if (window.history.pushState) {
        //     const newUrl = `/course/${course.id}/module/${nextModule.id}`;
        //     window.history.pushState({ moduleId: nextModule.id }, '', newUrl);
        //   }
        //   await loadModule(nextModule.id, modulesWithCurrent);
        //   toast.success(`🎉 Module completed! Now starting: "${nextModule.title}"`);
        // } else {
        //   setModules(updatedModules);
        //   toast.success('Module completed!');
        // }
      }

    } catch (error) {
      console.error('[CourseViewer] Error handling module completion:', error);
      toast.error('Failed to save progress');
    } finally {
      setCompletionLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      // Remove forced reload - this was causing authentication issues
      // The onClose callback already handles data refresh properly
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !currentModule) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Course</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleClose}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {onClose ? 'Go Back' : 'Try Again'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Completion Loading Overlay */}
      {completionLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Module Completed!</h3>
            <p className="text-gray-600">Loading next module...</p>
          </div>
        </div>
      )}

      {/* Module Navigation Sidebar */}
      <ModuleNavigation
        modules={modules}
        currentModuleId={currentModuleId || 0}
        onModuleSelect={handleModuleSelect}
        courseTitle={course?.title || 'Course'}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header with Close Button */}
        {onClose && (
          <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{currentModule?.title}</h1>
              <p className="text-sm text-gray-500 mt-1">
                Module {modules.findIndex(m => m.id === currentModuleId) + 1} of {modules.length}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close Course
            </button>
          </div>
        )}

        {/* Module Content */}
        <div className="flex-1 bg-white">
          {currentModule?.is_scorm ? (
            loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading SCORM content...</p>
                </div>
              </div>
            ) : scormUrl ? (
              <ScormWrapper
                courseId={String(course?.id || '')}
                moduleId={String(currentModule.id)}
                userId={String(user?.id || '')}
                courseUrl={scormUrl}
                initialProgress={currentModule.progress || 0}
                onError={(err) => {
                  setError(err.message);
                  toast.error(err.message);
                }}
                onProgress={(update) => {
                  setModules((prev) => prev.map((module) => (
                    module.id === currentModule.id
                      ? { ...module, progress: Math.max(module.progress || 0, update.progress) }
                      : module
                  )));
                }}
                onCompletion={handleModuleCompletion}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-red-600">
                  <p className="text-xl mb-2">Failed to load SCORM content</p>
                  <p className="text-sm text-gray-600">{error}</p>
                </div>
              </div>
            )
          ) : currentModule ? (
            <iframe
              src={currentModule.file_path}
              title={currentModule.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              onLoad={() => {
                // Mark module as started/in progress after content loads
                if (currentModuleId && course?.id) {
                  updateModuleProgress(currentModuleId, 50, course.id).catch(err => 
                    console.error('Failed to update progress:', err)
                  );
                }
                // Scroll to top when iframe content loads
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-600">
                <p className="text-xl mb-2">No module selected</p>
                <p className="text-sm">Select a module from the sidebar to begin</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};